import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Grid,
    Slide,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

// Import all swatches
import boneLeather from '../assets/leathers/swatches/bone.jpg';
import bottigliaLeather from '../assets/leathers/swatches/bottiglia.jpg';
import castagnoLeather from '../assets/leathers/swatches/castagno.jpg';
import cognacLeather from '../assets/leathers/swatches/cognac.jpg';
import grigioLeather from '../assets/leathers/swatches/grigio.jpg';
import mostoLeather from '../assets/leathers/swatches/mosto.jpg';
import napoliLeather from '../assets/leathers/swatches/napoli.jpg';
import nazionaleLeather from '../assets/leathers/swatches/nazionale.jpg';
import neroLeather from '../assets/leathers/swatches/nero.jpg';
import noceLeather from '../assets/leathers/swatches/noce.jpg';
import olivaLeather from '../assets/leathers/swatches/oliva.jpg';
import olmoLeather from '../assets/leathers/swatches/olmo.jpg';
import ortensiaLeather from '../assets/leathers/swatches/ortensia.jpg';
import papaveroLeather from '../assets/leathers/swatches/papavero.jpg';
import prugnaLeather from '../assets/leathers/swatches/prugna.jpg';
import ranticoLeather from '../assets/leathers/swatches/rantico.jpg';
import tabaccoLeather from '../assets/leathers/swatches/tabacco.jpg';
import whiskyLeather from '../assets/leathers/swatches/whisky.jpg';

// Import all textures
import boneLeatherTexture from '../assets/leathers/textures/bone.jpg';
import bottigliaLeatherTexture from '../assets/leathers/textures/bottiglia.jpg';
import castagnoLeatherTexture from '../assets/leathers/textures/castagno.jpg';
import cognacLeatherTexture from '../assets/leathers/textures/cognac.jpg';
import grigioLeatherTexture from '../assets/leathers/textures/grigio.jpg';
import mostoLeatherTexture from '../assets/leathers/textures/mosto.jpg';
import napoliLeatherTexture from '../assets/leathers/textures/napoli.jpg';
import nazionaleLeatherTexture from '../assets/leathers/textures/nazionale.jpg';
import neroLeatherTexture from '../assets/leathers/textures/nero.jpg';
import noceLeatherTexture from '../assets/leathers/textures/noce.jpg';
import olivaLeatherTexture from '../assets/leathers/textures/oliva.jpg';
import olmoLeatherTexture from '../assets/leathers/textures/olmo.jpg';
import ortensiaLeatherTexture from '../assets/leathers/textures/ortensia.jpg';
import papaveroLeatherTexture from '../assets/leathers/textures/papavero.jpg';
import prugnaLeatherTexture from '../assets/leathers/textures/prugna.jpg';
import ranticoLeatherTexture from '../assets/leathers/textures/rantico.jpg';
import tabaccoLeatherTexture from '../assets/leathers/textures/tabacco.jpg';
import whiskyLeatherTexture from '../assets/leathers/textures/whisky.jpg';

// Import WalletPreview component
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import WalletPreview from '../components/Configurator/WalletPreview';
import LeatherPicker from '../components/Configurator/pickers/LeatherPicker';
import { useCart } from '../contexts/CartContext';
import { useAppContext } from '../contexts/AppContext';

// Wallet options data
const walletOptions = [
    {
        id: 'marino',
        name: 'The Marino Bifold',
        description: 'Our signature Italian leather bifold with 4 card slots, thumb slot, and cash flap. Features dual-tone customization options.',
        image: 'https://i.etsystatic.com/51134566/r/il/1b9f04/6365775077/il_fullxfull.6365775077_ncba.jpg',
        price: 109,
        leatherType: 'Pueblo',
        engravingLocations: ['exterior', 'cash flap'],
        stitchingLocations: ['around perimeter'],
        usesAccentColor: true,
        pockets: [
            { id: 'exterior', name: 'Exterior', defaultColor: 'base' },
            { id: 'insideBack', name: 'Inside Back', defaultColor: 'exterior', hide: true },
            { id: 'insideLeft', name: 'Inside Left', defaultColor: 'base' },
            { id: 'thumbSlot', name: 'Thumb Slot', defaultColor: 'accent' },
            { id: 'insideRight', name: 'Inside Right', defaultColor: 'accent' },
            { id: 'cashFlap', name: 'Cash Flap', defaultColor: 'base' }
        ]
    },
    {
        id: 'palma',
        name: 'The Palma Card Holder',
        description: 'A sleek 3-pocket Italian leather card holder for a quick and stylish front-pocket carry. Features dual-tone customization options.',
        image: 'https://i.etsystatic.com/51134566/r/il/912c87/6571659536/il_fullxfull.6571659536_hnf2.jpg',
        price: 89,
        leatherType: 'Pueblo',
        engravingLocations: ['cash flap'],
        stitchingLocations: ['around perimeter'],
        usesAccentColor: true,
        pockets: [
            { id: 'thumbSlotBase', name: 'Thumb Slot Inside', defaultColor: 'base' },
            { id: 'thumbSlot', name: 'Thumb Slot', defaultColor: 'accent' },
            { id: 'cashFlapBase', name: 'Cash Flap Inside', defaultColor: 'base' },
            { id: 'cashFlap', name: 'Cash Flap', defaultColor: 'accent' }
        ]
    },
    {
        id: 'viaggio',
        name: 'The Viaggio Flap Wallet',
        description: 'Our flap wallet with 2 card slots, a cash flap, and a quick access tap-to-pay card slot. Features dual-tone customization options.',
        image: 'https://i.etsystatic.com/51134566/r/il/737b77/6571138160/il_fullxfull.6571138160_7gcj.jpg',
        price: 109,
        leatherType: 'Pueblo',
        engravingLocations: ['quick access slot'],
        stitchingLocations: ['quick access slot'],
        usesAccentColor: true,
        pockets: [
            { id: 'base', name: 'Base', defaultColor: 'base' },
            { id: 'accent', name: 'Accent', defaultColor: 'accent' },
        ]
    },
    // {
    //     id: 'passport',
    //     name: 'The Passport Wallet',
    //     description: 'Our flap wallet with 2 card slots, a cash flap, and a quick access tap-to-pay card slot. Features dual-tone customization options.',
    //     image: 'https://i.etsystatic.com/51134566/r/il/737b77/6571138160/il_fullxfull.6571138160_7gcj.jpg',
    //     price: 69,
    //     leatherType: 'Pueblo',
    //     engravingLocations: ['quick access slot'],
    //     stitchingLocations: ['quick access slot'],
    //     usesAccentColor: true,
    //     pockets: [
    //         { id: 'exterior', name: 'Exterior', defaultColor: 'base' },
    //         { id: 'interior', name: 'Interior', defaultColor: 'base' },
    //         { id: 'cashFlap', name: 'Cash Flap', defaultColor: 'accent' },
    //         { id: 'bottomRight', name: 'Bottom Right', defaultColor: 'base' },
    //         { id: 'topRight', name: 'Top Right', defaultColor: 'accent' },
    //     ]
    // },
    {
        id: 'minimo',
        name: 'The Minimo Wrap Wallet',
        description: 'A minimalist vertical wrap card holder with three pockets, including a cash flap on one side.',
        image: 'https://i.etsystatic.com/51134566/r/il/dd93a6/6367807029/il_600x600.6367807029_irbh.jpg',
        price: 51,
        leatherType: 'Pueblo',
        engravingLocations: ['cash flap'],
        stitchingLocations: ['bottom'],
        usesAccentColor: false,
        pockets: [
            { id: 'base', name: 'Base', defaultColor: 'base' },
        ]
    },
];

// Add leather color options (from your Etsy listing)
const leatherColors = [
    { id: 'bone', name: 'Bone (Natural)', hex: '#E8DCC4', swatch: boneLeather, texture: boneLeatherTexture },
    { id: 'bottiglia', name: 'Bottiglia (Emerald)', hex: '#1B4636', swatch: bottigliaLeather, texture: bottigliaLeatherTexture },
    { id: 'castagno', name: 'Castagno (Dark Brown)', hex: '#392E2C', swatch: castagnoLeather, texture: castagnoLeatherTexture },
    { id: 'cognac', name: 'Cognac', hex: '#B75D2C', swatch: cognacLeather, texture: cognacLeatherTexture },
    { id: 'grigio', name: 'Grigio (Grey)', hex: '#7B7B7B', swatch: grigioLeather, texture: grigioLeatherTexture },
    { id: 'mosto', name: 'Mosto (Bordeaux)', hex: '#4A1E1C', swatch: mostoLeather, texture: mostoLeatherTexture },
    { id: 'napoli', name: 'Napoli (Yellow)', hex: '#BF8A3C', swatch: napoliLeather, texture: napoliLeatherTexture },
    { id: 'nazionale', name: 'Nazionale (Sapphire)', hex: '#1B3B57', swatch: nazionaleLeather, texture: nazionaleLeatherTexture },
    { id: 'nero', name: 'Nero (Black)', hex: '#1C1C1C', swatch: neroLeather, texture: neroLeatherTexture },
    { id: 'noce', name: 'Noce (Walnut)', hex: '#5C3A31', swatch: noceLeather, texture: noceLeatherTexture },
    { id: 'olmo', name: 'Olmo', hex: '#8B7355', swatch: olmoLeather, texture: olmoLeatherTexture },
    { id: 'ortensia', name: 'Ortensia (Turquoise)', hex: '#3A7B7B', swatch: ortensiaLeather, texture: ortensiaLeatherTexture },
    { id: 'oliva', name: 'Oliva (Olive)', hex: '#4A4A2C', swatch: olivaLeather, texture: olivaLeatherTexture },
    { id: 'papavero', name: 'Papavero (Red)', hex: '#8B2C2C', swatch: papaveroLeather, texture: papaveroLeatherTexture },
    { id: 'prugna', name: 'Prugna (Violet)', hex: '#4A2C4A', swatch: prugnaLeather, texture: prugnaLeatherTexture },
    { id: 'rantico', name: 'R. Antico (Pink)', hex: '#BC8F8F', swatch: ranticoLeather, texture: ranticoLeatherTexture },
    { id: 'tabacco', name: 'Tabacco', hex: '#8B5A2B', swatch: tabaccoLeather, texture: tabaccoLeatherTexture },
    { id: 'whisky', name: 'Whisky (Siena)', hex: '#8B4513', swatch: whiskyLeather, texture: whiskyLeatherTexture }
];

const threadColors = [
    { id: 'white', name: 'JK1 White', hex: '#ffffff' },
    { id: 'orange', name: 'JK247 Orange', hex: '#e85319' },
    { id: 'purple', name: 'JK80 Purple', hex: '#5b2c83' },
    { id: 'cream', name: 'JK6 Cream', hex: '#fffad2' },
    { id: 'red', name: 'JK62 Red', hex: '#bc171c' },
    { id: 'royalblue', name: 'JK14 Royal Blue', hex: '#253160' },
    { id: 'beige', name: 'JK2 Beige', hex: '#d4a891' },
    { id: 'colonialtan', name: 'JK79 Colonial Tan', hex: '#c28846' },
    { id: 'yellow', name: 'JKt56 Yellow', hex: '#fcd100' },
    { id: 'silver', name: 'JK85 Silver', hex: '#d9d8d8' },
    { id: 'havannacigar', name: 'JK77 Havanna Cigar', hex: '#7a5642' },
    { id: 'pink', name: 'Pink Peony', hex: '#f67b7c' },
    { id: 'spacegrey', name: 'JK4 Space Grey', hex: '#303731' },
    { id: 'peagreen', name: 'JK86 Pea Green', hex: '#6b6a20' },
    { id: 'midbrown', name: 'JK7 Mid Brown', hex: '#2e2516' },
    { id: 'black', name: 'JK23 Black', hex: '#000' },
    { id: 'green', name: 'JK11 Green', hex: '#143f3b' },
    { id: 'brown', name: 'JK21 Brown', hex: '#352c1a' },
];

const favoriteCombos = [
    { id: 'olmoteal', name: 'Olmo & Ortensia, Colonial Tan', base: 'olmo', accent: 'ortensia', thread: 'colonialtan' },
    { id: 'bluered', name: 'Nazionale & Papavero, Royal Blue', base: 'nazionale', accent: 'papavero', thread: 'royalblue' },
    { id: 'whiskymosto', name: 'Whisky & Mosto, Cream', base: 'whisky', accent: 'mosto', thread: 'cream' },
]

function Configurator() {
    const [activeStep, setActiveStep] = useState(0);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [slideDirection, setSlideDirection] = useState('left');
    const [baseColor, setBaseColor] = useState('noce');
    const [accentColor, setAccentColor] = useState('ortensia');
    const [stitchColor, setStitchColor] = useState('beige');
    const [selectedCombo, setSelectedCombo] = useState(null);
    const [engraving, setEngraving] = useState('');
    const [pocketColors, setPocketColors] = useState({});
    
    const { addToCart } = useCart();
    const { showSnackbar } = useAppContext();
    const navigate = useNavigate();

    const [query] = useSearchParams();

    useEffect(() => {
        const baseColor = query.get('baseColor');
        const accentColor = query.get('accentColor');
        const stitchColor = query.get('stitchColor');
        const selectedCombo = query.get('selectedCombo');
        const engraving = query.get('engraving') ?? '';
        const pocketColors = query.get('pocketColors');
        const selectedWallet = query.get('design');

        if (selectedWallet) {
            setSelectedWallet(walletOptions.find(a => a.id === selectedWallet));
        } else if (baseColor || accentColor || stitchColor || selectedCombo || pocketColors) {
            // default to Marino for older URLs
            setSelectedWallet(walletOptions[0]);
        }

        if (selectedCombo) setSelectedCombo(selectedCombo);
        if (baseColor) setBaseColor(baseColor);
        if (accentColor) setAccentColor(accentColor);
        if (stitchColor) setStitchColor(stitchColor);
        if (engraving) setEngraving(engraving);
        if (pocketColors) setPocketColors(JSON.parse(pocketColors));

        if (baseColor || accentColor || stitchColor || selectedCombo || pocketColors) {
            setActiveStep(2);
        }
    }, [query]);

    const steps = [
        'Wallet style',
        'Starting color combo',
        'Customizations'
    ];

    const handleWalletSelect = (walletId) => {
        const wallet = walletOptions.find(a => a.id === walletId);
        if (wallet) {
            setSelectedWallet(wallet);
            setSlideDirection('left');
            setActiveStep(1);
        }
    };

    const handleBack = () => {
        switch (activeStep) {
            case 0:
                return;
            case 1:
                setSlideDirection('right');
                setSelectedWallet(null)
                setSelectedCombo(null);
                setActiveStep(0);
                return;
            case 2:
                setSlideDirection('right');
                setSelectedCombo(null);
                setActiveStep(1);
                return;
            default:
                setSlideDirection('right');
                setActiveStep((prev) => prev - 1);
                return;
        };
    };

    const getEffectiveColor = (pocketId) => {
        // quick win: find it in the pocketColors state
        if (pocketId in pocketColors) {
            return pocketColors[pocketId];
        }

        // find default color for the pocket
        const pocketDefaultColor = selectedWallet?.pockets.find(a => a.id === pocketId)?.defaultColor;

        if (pocketDefaultColor === 'base') {
            return baseColor;
        } else if (pocketDefaultColor === 'accent') {
            return accentColor;
        } else if (pocketDefaultColor in pocketColors) {
            return pocketColors[pocketDefaultColor];
        }

        // if all else fails, return base color
        return baseColor;
    };

    const generateShareUrl = async () => {
        const url = new URL(window.location.href);
        url.searchParams.set('design', selectedWallet.id);
        url.searchParams.set('selectedCombo', selectedCombo);
        url.searchParams.set('baseColor', baseColor);
        url.searchParams.set('accentColor', accentColor);
        url.searchParams.set('pocketColors', JSON.stringify(pocketColors));
        url.searchParams.set('stitchColor', stitchColor);
        if (engraving?.trim()) {
            url.searchParams.set('engraving', engraving);
        }

        // Shorten URL using shrtco.de API
        return fetch(`https://tinyurl.com/api-create.php?url=${encodeURIComponent(url.toString())}`)
            .then(response => response.text())
            .then(shortUrl => shortUrl || url.toString())
            .catch(() => url.toString());
    }

    const resetColors = () => {
        // create pocket colors from the selected wallet
        const updatedColors = selectedWallet?.pockets.reduce((acc, pocket) => {
            acc[pocket.id] = pocket.defaultColor === 'base' ? baseColor : accentColor;
            return acc;
        }, {});
        setPocketColors(updatedColors);
    };

    const handleComboSelect = (combo) => {
        setSelectedCombo(combo.id);
        setBaseColor(combo.base);
        setAccentColor(combo.accent);
        setStitchColor(threadColors.find(a => a.id === combo.thread)?.hex);

        // create pocket colors from the selected wallet
        const updatedColors = selectedWallet?.pockets.reduce((acc, pocket) => {
            if (pocket.defaultColor === 'base') {
                acc[pocket.id] = combo.base;
            } else if (pocket.defaultColor === 'accent') {
                acc[pocket.id] = combo.accent;
            }
            return acc;
        }
            , {});
        setPocketColors(updatedColors);

        setActiveStep(2);
    }

    const handleEngravingChange = (event) => {
        setEngraving(event.target.value);
    }
    
    const handleAddToCart = () => {
        if (!selectedWallet) return;
        
        // Find the selected wallet from walletOptions
        const walletProduct = walletOptions.find(wallet => wallet.id === selectedWallet.id);
        
        if (!walletProduct) return;
        
        // Create a product object for the cart
        const product = {
            id: walletProduct.id,
            name: walletProduct.name,
            price: walletProduct.price,
            image: walletProduct.image,
            images: [{ src: walletProduct.image, alt: walletProduct.name }]
        };
        
        // Create configurator options object
        const configuratorOptions = {
            baseColor,
            accentColor,
            stitchColor,
            pocketColors,
            engraving: engraving.trim() || undefined
        };
        
        // Add to cart
        addToCart(product, 1, {}, configuratorOptions);
        
        // Show success message
        showSnackbar(`${walletProduct.name} added to cart`, 'success');
        
        // Navigate to cart
        navigate('/checkout');
    };

    const handleBaseOrAccentLeatherSelection = (pocketId, colorId) => {
        if (pocketId === 'baseColor') {
            setBaseColor(colorId);
            // update colors for base pockets
            const updatedColors = selectedWallet?.pockets.reduce((acc, pocket) => {
                if (pocket.defaultColor === 'base') {
                    acc[pocket.id] = colorId;
                }
                return acc;
            }, {});
            setPocketColors(prev => ({ ...prev, ...updatedColors }));
        } else if (pocketId === 'accentColor') {
            setAccentColor(colorId);
            // update colors for accent pockets
            const updatedColors = selectedWallet?.pockets.reduce((acc, pocket) => {
                if (pocket.defaultColor === 'accent') {
                    acc[pocket.id] = colorId;
                }
                return acc;
            }, {});
            setPocketColors(prev => ({ ...prev, ...updatedColors }));
        }
    };

    const handleLeatherSelection = (pocketId, colorId) => {
        setPocketColors(prev => ({
            ...prev,
            [pocketId]: colorId
        }));
    };

    const renderStep = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box paddingBottom={2}>
                        <Typography variant="h5" sx={{ mb: 3 }}>
                            Select your wallet style
                        </Typography>
                        <Grid container spacing={4}>
                            {walletOptions.map((wallet) => (
                                <Grid item xs={12} md={6} key={wallet.id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: wallet.comingsoon ? 'not-allowed' : 'pointer',
                                            '&:hover': {
                                                boxShadow: wallet.comingsoon ? 0 : 6,
                                                transform: wallet.comingsoon ? 'none' : 'translateY(-4px)',
                                            },
                                            transition: 'all 0.3s ease-in-out',
                                            border: selectedWallet?.id === wallet.id ? 2 : 0,
                                            borderColor: 'primary.main',
                                            position: 'relative'
                                        }}
                                        onClick={() => !wallet.comingsoon && handleWalletSelect(wallet.id)}
                                    >
                                        {wallet.comingsoon && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    color: 'white',
                                                    fontSize: '1.5rem',
                                                    zIndex: 1
                                                }}
                                            >
                                                Coming Soon
                                            </Box>
                                        )}
                                        <CardMedia
                                            component="img"
                                            sx={{
                                                height: 300,
                                                objectFit: 'cover'
                                            }}
                                            image={wallet.image}
                                            alt={wallet.name}
                                        />
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography gutterBottom variant="h5" component="h2">
                                                {wallet.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {wallet.description}
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                ${wallet.price}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                sx={{ mt: 2 }}
                                                onClick={() => !wallet.comingsoon && handleWalletSelect(wallet.id)}
                                                disabled={wallet.comingsoon}
                                                data-testid={`wallet-${wallet.id}-button`}
                                            >
                                                Select & Customize
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            case 1:
                return (
                    <Box paddingBottom={2}>
                        <Typography variant="h5" sx={{ mb: 4 }}>
                            Choose a starting combination
                        </Typography>

                        <Grid container spacing={4}>
                            {favoriteCombos.map((combo) => {
                                const comboBase = leatherColors.find((c) => c.id === combo.base);
                                const comboAccent = leatherColors.find((c) => c.id === combo.accent);
                                const comboThread = threadColors.find((c) => c.id === combo.thread);
                                return (
                                <Grid item xs={12} md={6} key={combo.id}>
                                    <Card
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                boxShadow: 6,
                                                transform: 'translateY(-4px)',
                                            },
                                            transition: 'all 0.3s ease-in-out',
                                            border: selectedCombo === combo.id ? 2 : 0,
                                            borderColor: 'primary.main'
                                        }}
                                        onClick={() => handleComboSelect(combo)}
                                    >
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                {[comboBase, comboAccent].map((leather) => leather && (
                                                    <Box
                                                        key={leather.id}
                                                        component="img"
                                                        src={leather.swatch}
                                                        alt={`${leather.name} leather swatch`}
                                                        title={leather.name}
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: 1,
                                                            objectFit: 'cover',
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            bgcolor: leather.hex,
                                                        }}
                                                    />
                                                ))}
                                                {comboThread && (
                                                    <Box
                                                        role="img"
                                                        aria-label={`${comboThread.name} thread`}
                                                        title={`${comboThread.name} thread`}
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: '50%',
                                                            bgcolor: comboThread.hex,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography gutterBottom variant="h5" component="h2">
                                                {combo.name}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                sx={{ mt: 2 }}
                                                onClick={() => handleComboSelect(combo)}
                                                data-testid={`combo-${combo.id}-button`}
                                            >
                                                Select & Customize
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                );
                            })}
                        </Grid>
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={handleBack}>Back</Button>
                        </Box>
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={5} lg={4}>
                                <Typography variant="h5" sx={{ mb: 0, mx: 2 }} data-testid={"wallet-configurator-header"}>
                                    {selectedWallet?.name}
                                </Typography>
                                <Accordion defaultExpanded>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel1-content"
                                        id="panel1-header"
                                    >
                                        <span>Base</span>
                                        {selectedWallet?.usesAccentColor && (
                                            <span>&nbsp;and accent colors</span>
                                        )}
                                        {!selectedWallet?.usesAccentColor && (
                                            <span>&nbsp;color</span>
                                        )}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{
                                            maxHeight: { xs: '40vh', md: '70vh' },
                                            overflowY: 'auto',
                                            pr: 2
                                        }}>
                                            <LeatherPicker key="baseColor" pocket={{ id: 'baseColor', name: 'Base', effective: baseColor }} effectiveColor={baseColor} leatherColors={leatherColors} colorSelectionHandler={handleBaseOrAccentLeatherSelection} />
                                            {selectedWallet?.usesAccentColor && (
                                                <LeatherPicker key="accentColor" pocket={{ id: 'accentColor', name: 'Accent', effective: accentColor }} effectiveColor={accentColor} leatherColors={leatherColors} colorSelectionHandler={handleBaseOrAccentLeatherSelection} />
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion data-testid="thread-color-accordion">
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel2-content"
                                        id="panel2-header"
                                    >
                                        Thread color
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{
                                            maxHeight: { xs: '40vh', md: '70vh' },
                                            overflowY: 'auto',
                                            pr: 2
                                        }}>

                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {stitchColor && threadColors.find(a => a.hex === stitchColor)?.name}
                                            </Typography>
                                            <Box key="threadColor" sx={{ mb: 3 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                    padding: 1
                                                }}>
                                                    {threadColors.map((color) => (
                                                        <Tooltip title={color.name} key={color.id}>
                                                            <Box
                                                                onClick={() => setStitchColor(color.hex)}
                                                                data-testid={`Thread-${color.name}`}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    overflow: 'hidden',
                                                                    cursor: 'pointer',
                                                                    filter: stitchColor === color.hex ? 'drop-shadow(0px 0px 4px #00F)' : null,
                                                                    border: '2px solid',
                                                                    borderColor: stitchColor === color.hex ? 'primary.main' : 'divider',
                                                                    backgroundColor: color.hex,
                                                                    backgroundSize: 'cover',
                                                                    backgroundPosition: 'center',
                                                                    '&:hover': {
                                                                        transform: 'scale(1.1)',
                                                                    },
                                                                    transition: 'all 0.2s ease-in-out'
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion data-testid="engraving-accordion">
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel3-content"
                                        id="panel3-header"
                                    >
                                        Engraving
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{
                                            maxHeight: { xs: '40vh', md: '70vh' },
                                            overflowY: 'auto',
                                            pr: 2
                                        }}>

                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Initials or name
                                            </Typography>
                                            <TextField value={engraving} onChange={handleEngravingChange} inputProps={{ 'data-testid': 'engraving-text' }} />
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel4-content"
                                        id="panel4-header"
                                    >
                                        Customize individual pockets
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{
                                            maxHeight: { xs: '40vh', md: '70vh' },
                                            overflowY: 'auto',
                                            pr: 2
                                        }}>
                                            {selectedWallet?.pockets.filter(a => !a.hide).map((pocket) => (
                                                <LeatherPicker key={pocket.id} pocket={pocket} effectiveColor={getEffectiveColor(pocket.id)} leatherColors={leatherColors} colorSelectionHandler={handleLeatherSelection} />
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>

                            <Grid item xs={12} md={7} lg={8}>
                                <Box sx={{
                                    width: '100%',
                                    aspectRatio: '1',
                                }}>
                                    <WalletPreview
                                        walletType={selectedWallet?.id}
                                        leatherColors={leatherColors}
                                        threadColors={threadColors}
                                        getEffectiveColor={getEffectiveColor}
                                        stitchColor={stitchColor}
                                        engraving={engraving}
                                        resetColors={resetColors}
                                        generateShareUrl={generateShareUrl}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Button onClick={handleBack}>Back</Button>
                            <Box>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    startIcon={<AddShoppingCartIcon />}
                                    onClick={() => handleAddToCart()}
                                    sx={{ mr: 2 }}
                                >
                                    Add to Cart
                                </Button>
                                <Button variant="outlined" color="primary" onClick={() => {
                                    window.history.replaceState({}, document.title, window.location.pathname);
                                    window.location.reload();
                                }}>Restart</Button>
                            </Box>
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ marginBottom: 4 }}>
                Wallet Designer
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <Slide
                    direction={slideDirection}
                    in={true}
                    mountOnEnter
                    unmountOnExit
                    timeout={350}
                >
                    <Box>
                        {renderStep(activeStep)}
                    </Box>
                </Slide>
            </Box>
        </Container>
    );
}

export default Configurator; 